import { Node, mergeAttributes, nodePasteRule } from "@tiptap/core";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    videoEmbed: {
      setVideoEmbed: (options: { src: string }) => ReturnType;
    };
  }
}

function getEmbedUrl(url: string): string | null {
  // YouTube: youtube.com/watch?v=ID, youtu.be/ID, youtube.com/embed/ID
  const ytMatch = url.match(
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]+)/,
  );
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;

  // Vimeo: vimeo.com/ID
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;

  return null;
}

/**
 * Matches YouTube and Vimeo URLs for paste rules.
 * Captures: youtube.com/watch?v=..., youtu.be/..., vimeo.com/...
 */
const VIDEO_URL_REGEX =
  /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/|vimeo\.com\/)\S+/g;

export const VideoEmbed = Node.create({
  name: "videoEmbed",

  group: "block",

  atom: true,

  addAttributes() {
    return {
      src: {
        default: null,
        parseHTML: (element) => element.querySelector("iframe")?.getAttribute("src"),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-video-embed="true"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const src = HTMLAttributes.src as string | null;

    return [
      "div",
      mergeAttributes(
        {
          "data-video-embed": "true",
          class: "video-embed",
          style:
            "position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; max-width: 100%; margin: 1rem 0;",
        },
        // Don't pass src to the wrapper div
        { ...HTMLAttributes, src: undefined },
      ),
      [
        "iframe",
        {
          src,
          frameborder: "0",
          allowfullscreen: "true",
          allow:
            "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture",
          style:
            "position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: 0;",
        },
      ],
    ];
  },

  addCommands() {
    return {
      setVideoEmbed:
        ({ src }) =>
        ({ commands }) => {
          const embedUrl = getEmbedUrl(src) ?? src;
          return commands.insertContent({
            type: this.name,
            attrs: { src: embedUrl },
          });
        },
    };
  },

  addPasteRules() {
    return [
      nodePasteRule({
        find: VIDEO_URL_REGEX,
        type: this.type,
        getAttributes: (match) => {
          const url = match[0];
          const embedUrl = getEmbedUrl(url);
          if (!embedUrl) return false;
          return { src: embedUrl };
        },
      }),
    ];
  },
});
